import { useMemo } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import useSWR from 'swr';

import { GroupedRoles, IRestaurant, IRestaurantFoodCard, Meal } from 'src/types/user';
import axiosInstance, { endpoints, fetcher } from 'src/utils/axios';
import { components } from 'src/types/schema';
import { client, joinErrors, useQuery } from './client';

// ----------------------------------------------------------------------

// Get current server date and time
export async function getCurrentDateTime(): Promise<components["schemas"]["CommonCurrentDateTime"]> {
  const response = await client.GET('/current-date-time');
  if (response.error) {
    throw new Error(joinErrors(response.error));
  }
  return response.data;
}

export function useGetCurrentDateTime() {
  const { data, isLoading, error, isValidating } = useQuery('/current-date-time');

  const memoizedValue = useMemo(
    () => ({
      currentDateTime: data,
      currentDateTimeLoading: isLoading,
      currentDateTimeError: error,
      currentDateTimeValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

const shouldFetch = <T>(init: T, id: number): T | null => (id > 0 ? init : null);

export function useGetRestaurants() {
  const { data, isLoading, error, isValidating } = useQuery('/partners/restaurants/');

  const memoizedValue = useMemo(
    () => ({
      restaurants: data?.results || [],
      restaurantsLoading: isLoading,
      restaurantsError: error,
      restaurantsValidating: isValidating,
      restaurantsEmpty: !isLoading && !data?.results?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetSubuserPermissions() {
  const URL = endpoints.subusers.permissions;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      permissions: (data?.results as { id: string; name: string }[]) || [],
      permissionsLoading: isLoading,
      permissionsError: error,
      permissionsValidating: isValidating,
      permissionsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetSubuserRoles() {
  const URL = endpoints.subusers.roles;

  const { data, isLoading, error } = useSWR(URL, fetcher, { dedupingInterval: 300000 });

  const memoizedValue = useMemo(
    () => ({
      roles: (data?.results as GroupedRoles[]) || [],
      rolesLoading: isLoading,
      rolesError: error,
    }),
    [data, error, isLoading]
  );

  return memoizedValue;
}

export function useGetMyRestaurants() {
  const URL = endpoints.restaurants.list_sub;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, { errorRetryCount: 2 });

  localStorage.setItem('restaurant_id', data?.[0]?.id || '');

  const memoizedValue = useMemo(
    () => ({
      restaurants: (data as IRestaurant[]) || [],
      restaurantsLoading: isLoading,
      restaurantsError: error,
      restaurantsValidating: isValidating,
      restaurantsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetRestaurant(id: number) {
  const { data, isLoading, error, isValidating } = useQuery(
    `/partners/restaurants/{id}/`,
    shouldFetch(
      {
        params: {
          path: { id },
        },
      },
      id
    )
  );

  const memoizedValue = useMemo(
    () => ({
      restaurant: data,
      restaurantLoading: isLoading,
      restaurantError: error,
      restaurantValidating: isValidating,
      restaurantEmpty: !isLoading && error,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetRestaurantPlanGroups(restaurant_id: string | undefined, search: string) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/plangroups/',
    shouldFetch(
      {
        params: {
          query: {
            restaurant: restaurant_id ? Number(restaurant_id) : undefined,
            search: search && search.length > 0 ? search : undefined,
          },
        },
      },
      Number(restaurant_id)
    )
  );

  const memoizedValue = useMemo(
    () => ({
      restaurantPlanGroups: data?.results || [],
      restaurantPlanGroupsLoading: isLoading,
      restaurantPlanGroupsError: error,
      restaurantPlanGroupsValidating: isValidating,
      restaurantPlanGroupsEmpty: !isLoading && !data?.results?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export type RestaurantFoodsResponse = components['schemas']['PaginatedPartnersFoodList'];
export type RestaurantFoods = RestaurantFoodsResponse['results'];
export function useGetRestaurantFoods(
  id: string | undefined,
  page: number,
  page_size: number,
  search: string
) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/foods/',
    shouldFetch(
      {
        params: {
          query: {
            restaurant: id ? Number(id) : undefined,
            search: search && search.length > 0 ? search : undefined,
            page: page ? Number(page) : undefined,
            page_size: page_size ? Number(page_size) : undefined,
          },
        },
      },
      Number(id)
    )
  );

  const memoizedValue = useMemo(
    () => ({
      restaurantFoods: data,
      restaurantFoodsLoading: isLoading,
      restaurantFoodsError: error,
      restaurantFoodsValidating: isValidating,
      restaurantFoodsEmpty: !isLoading && !data?.results.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export type RestaurantFood = components['schemas']['PartnersFood'];
export function useGetRestaurantFood(id: string | undefined) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/foods/{id}/',
    shouldFetch(
      {
        params: {
          path: { id: Number(id) },
        },
      },
      Number(id)
    )
  );

  const memoizedValue = useMemo(
    () => ({
      restaurantFood: data,
      restaurantFoodLoading: isLoading,
      restaurantFoodError: error,
      restaurantFoodValidating: isValidating,
      restaurantFoodEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetRestaurantMeals(id: string | undefined) {
  const URL = id ? endpoints.restaurants.meals(id) : '';
  const INGREDIENT_URL = endpoints.foods.ingredientsList;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const { data: ingredients, isLoading: ingredientsLoading } = useSWR(INGREDIENT_URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      ingredients: {
        carb_categories: ingredients?.carb_categories || [],
        protein_categories: ingredients?.protein_categories || [],
        ingredientsLoading,
      },
      restaurantMeals: (data as Meal[]) || [],
      mealsLoading: isLoading,
      mealsError: error,
      mealsValidating: isValidating,
      mealsEmpty: !isLoading && !data?.length,
    }),
    [
      data,
      error,
      ingredients?.carb_categories,
      ingredients?.protein_categories,
      ingredientsLoading,
      isLoading,
      isValidating,
    ]
  );

  return memoizedValue;
}

export async function uploadRestaurantImage(id: string | undefined, image_files: any) {
  const formData = new FormData();
  if (image_files.logo) {
    formData.append('logo', image_files.logo);
  }
  if (image_files.cover) {
    formData.append('cover', image_files.cover);
  }
  formData.append('id', id || '');

  const { data, error } = await client.PATCH('/partners/restaurants/{id}/images/', {
    params: {
      path: { id: Number(id) },
    },
    body: formData as any,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error uploading restaurant image');
  }

  return data;
}

export async function uploadFoodImage(id: string, image_file: any) {
  try {
    const formData = new FormData();
    formData.append('image', image_file);

    const response = await client.PUT('/partners/foods/{id}/image/', {
      params: {
        path: { id: Number(id) },
      },
      body: formData as any,
    });

    if (response.error) {
      const errorMessage = response.error.errors
        ? joinErrors(response.error)
        : (response.error as any).detail || 'Error uploading food image';
      throw new Error(errorMessage);
    }

    // After successful upload, fetch the updated food data to get the new image URL
    const { data: updatedFood } = await client.GET('/partners/foods/{id}/', {
      params: {
        path: { id: Number(id) },
      },
    });

    return updatedFood;
  } catch (error) {
    console.error('Food image upload error:', error);
    throw error;
  }
}

export async function deleteFoodImage(id: string) {
  try {
    const response = await client.PATCH('/partners/foods/{id}/image/', {
      params: {
        path: { id: Number(id) },
      },
      body: { image: null } as any,
    });

    if (response.error) {
      const errorMessage = response.error.errors
        ? joinErrors(response.error)
        : (response.error as any).detail || 'Error deleting food image';
      throw new Error(errorMessage);
    }

    // After successful deletion, fetch the updated food data
    const { data: updatedFood } = await client.GET('/partners/foods/{id}/', {
      params: {
        path: { id: Number(id) },
      },
    });

    return updatedFood;
  } catch (error) {
    console.error('Food image deletion error:', error);
    throw error;
  }
}

export async function updateRestaurantData(data: any) {
  const { id, ...updateData } = data;
  const { data: response, error } = await client.PATCH('/partners/restaurants/{id}/', {
    params: {
      path: { id: Number(id) },
    },
    body: updateData,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error updating restaurant data');
  }

  return response;
}

export async function updateRestaurantPassword(data: any) {
  const URL = endpoints.restaurants.update_password;
  const response = await axiosInstance.post(URL, data);

  if (response.status >= 200 && response.status < 300) {
    const response_data = response.data;
    return response_data;
  }

  if (response) {
    throw new Error(response.data);
  }

  throw new Error('Error changing password');
}

// TODO: update this function once the backend is updated
export async function deleteRestaurant(id: string) {
  const URL = endpoints.restaurants.delete(id);
  const response = await axiosInstance.delete(URL);
  if (response.status >= 200 && response.status < 300) {
    const response_data = response.data;
    return response_data;
  }
  throw new Error('Error deleting restaurant data');
}

type RestaurantRequest = components['schemas']['PartnersRestaurantRequest'];
export async function createNewRestaurant(restaurant: RestaurantRequest) {
  const { data, error } = await client.POST('/partners/restaurants/', {
    body: restaurant,
  });
  if (error) {
    throw new Error(joinErrors(error) || 'Error creating restaurant data');
  }

  return data;
}

export async function createNewRestaurantFood(data: any) {
  // Ensure only one availability type is sent
  const { availability_patterns, availability_dates, ...rest } = data || {};
  const sanitizedBody =
    data?.availability_type === 'recurring'
      ? { ...rest, availability_patterns }
      : data?.availability_type === 'specific'
        ? { ...rest, availability_dates }
        : { ...rest };

  const response = await client.POST('/partners/foods/', {
    body: sanitizedBody,
  });
  if (response.error) {
    throw new Error(joinErrors(response.error) || 'Error creating new food');
  }

  return response.data;
}

export async function updateRestaurantFood(data: any) {
  // Ensure only one availability type is sent
  const { availability_patterns, availability_dates, ...rest } = data || {};
  const sanitizedBody =
    data?.availability_type === 'recurring'
      ? { ...rest, availability_patterns }
      : data?.availability_type === 'specific'
        ? { ...rest, availability_dates }
        : { ...rest };

  const response = await client.PATCH('/partners/foods/{id}/', {
    params: {
      path: { id: Number(data.id) },
    },
    body: sanitizedBody,
  });
  if (response.error) {
    throw new Error(joinErrors(response.error) || 'Error updating food');
  }

  return response.data;
}

export async function getRestaurantDeliveryTime(restaurant_slug: string) {
  try {
    const URL = endpoints.restaurants.delievery_time(restaurant_slug);
    const res = await axiosInstance.get(URL);
    return res.data;
  } catch (error) {
    console.error('Failed to fetch delivery times:', error);
    throw error;
  }
}

export async function updateRestaurantDeliveryTime(
  delivery_time_id: number,
  data: { start_time: string; end_time: string }
) {
  try {
    const URL = endpoints.restaurants.select_delivery_time(delivery_time_id.toString());
    const res = await axiosInstance.put(URL, data);
    return res;
  } catch (error) {
    // Handle error here. Could be due to invalid data, server issues, etc.
    console.error('Failed to update delivery time:', error);
    throw error;
  }
}

export async function createRestaurantDeliveryTime(
  subscription_id: string,
  data: { start_time: string; end_time: string; original_delivery_time: number }
) {
  try {
    const URL = endpoints.restaurants.select_create_delivery_time(subscription_id);
    const res = await axiosInstance.post(URL, data);
    return res;
  } catch (error) {
    // Handle error here. Could be due to invalid data, server issues, etc.
    console.error('Failed to update delivery time:', error);
    throw error;
  }
}

export async function updateRestaurantDeliveryTimeSubadmin(
  restaurant_slug: string,
  data: { id?: number; start_time: string; end_time: string }[]
) {
  try {
    const URL = endpoints.restaurants.delievery_time(restaurant_slug.toString());
    const res = await axiosInstance.post(URL, data);
    return res;
  } catch (error) {
    // Handle error here. Could be due to invalid data, server issues, etc.
    console.error('Failed to post delivery time:', error);
    throw error;
  }
}

export async function createNewRestaurantPlanGroup(data: any) {
  const { data: response_data, error } = await client.POST('/partners/plangroups/', {
    body: data,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error creating plan group');
  }

  return response_data;
}

export async function updateRestaurantPlanGroup(data: any) {
  const { id, ...updateData } = data;
  const { data: response_data, error } = await client.PATCH('/partners/plangroups/{id}/', {
    params: {
      path: { id: Number(id) },
    },
    body: updateData,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error updating plan group');
  }

  return response_data;
}

export async function deleteRestaurantPlanGroup(id: number) {
  const { data: response_data, error } = await client.PATCH('/partners/plangroups/{id}/', {
    params: {
      path: { id },
    },
    body: {
      is_deleted: true,
      plans_count: 0,
    },
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error deleting plan group');
  }

  return response_data;
}

export type RestaurantPlanGroup = components['schemas']['PartnersPlanGroup'];
export function useGetRestaurantPlanGroup(id: number) {
  const { data, isLoading, error, isValidating, mutate } = useQuery(
    '/partners/plangroups/{id}/',
    shouldFetch(
      {
        params: {
          path: { id },
        },
      },
      Number(id)
    )
  );

  const memoizedValue = useMemo(
    () => ({
      restaurantPlanGroup: data,
      restaurantPlanGroupLoading: isLoading,
      restaurantPlanGroupError: error,
      restaurantPlanGroupValidating: isValidating,
      restaurantPlanGroupEmpty: !isLoading && !data,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export type CommonMealTag = components['schemas']['CommonMealTag'];
export function useGetMealTags() {
  const { data, isLoading, error, isValidating } = useQuery('/meal-tags', {
    params: {
      query: {
        version: 'v1',
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      mealTags: data || [],
      mealTagsLoading: isLoading,
      mealTagsError: error,
      mealTagsValidating: isValidating,
      mealTagsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export type CommonPlanTag = components['schemas']['CommonPlanTag'];
export function useGetPlanTags() {
  const { data, isLoading, error, isValidating } = useQuery('/plan-tags', {
    params: {
      query: {
        version: 'v1',
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      planTags: data || [],
      planTagsLoading: isLoading,
      planTagsError: error,
      planTagsValidating: isValidating,
      planTagsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetRestaurantSubscriptions(
  restaurant_id: string | undefined,
  page: number,
  page_size: number,
  search: string,
  status: 'active' | 'cancelled' | 'old' | 'unpaid' | '',
  start_date: string | undefined,
  end_date: string | undefined,
  created_at_lte: string | undefined,
  created_at_gte: string | undefined
) {
  const { data, isLoading, error, isValidating } = useQuery('/partners/subscriptions/', {
    params: {
      query: {
        restaurant: restaurant_id ? Number(restaurant_id) : undefined,
        page: page ? Number(page) : undefined,
        page_size: page_size ? Number(page_size) : undefined,
        search: search && search.length > 0 ? search : undefined,
        status:
          status && status.length > 0
            ? (status as 'active' | 'cancelled' | 'old' | 'unpaid')
            : undefined,
        start_date: start_date && start_date.length > 0 ? start_date : undefined,
        end_date: end_date && end_date.length > 0 ? end_date : undefined,
        created_at_lte: created_at_lte && created_at_lte.length > 0 ? created_at_lte : undefined,
        created_at_gte: created_at_gte && created_at_gte.length > 0 ? created_at_gte : undefined,
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      restaurantSubscriptions: data,
      restaurantSubscriptionsLoading: isLoading,
      restaurantSubscriptionsError: error,
      restaurantSubscriptionsValidating: isValidating,
      restaurantSubscriptionsEmpty: !isLoading && !data?.results?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProteinCategories() {
  const { data, isLoading, error, isValidating } = useQuery('/protein-categories', {
    params: {
      query: {
        version: 'v1',
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      proteinCategories: data,
      proteinCategoriesLoading: isLoading,
      proteinCategoriesError: error,
      proteinCategoriesValidating: isValidating,
      proteinCategoriesEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetCarbCategories() {
  const { data, isLoading, error, isValidating } = useQuery('/carb-categories', {
    params: {
      query: {
        version: 'v1',
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      carbCategories: data,
      carbCategoriesLoading: isLoading,
      carbCategoriesError: error,
      carbCategoriesValidating: isValidating,
      carbCategoriesEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetDays() {
  const { data, isLoading, error, isValidating } = useQuery('/days', {
    params: {
      query: {
        version: 'v1',
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      days: data,
      daysLoading: isLoading,
      daysError: error,
      daysValidating: isValidating,
      daysEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetSelectedFoods(search?: string, date_gte?: string, date_lte?: string) {
  const { data, isLoading, error, isValidating } = useQuery('/partners/selectedfoods-foods/', {
    params: {
      query: {
        search: search && search.length > 0 ? search : undefined,
        date_gte: date_gte && date_gte.length > 0 ? date_gte : undefined,
        date_lte: date_lte && date_lte.length > 0 ? date_lte : undefined,
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      selectedFoods: data,
      selectedFoodsLoading: isLoading,
      selectedFoodsError: error,
      selectedFoodsValidating: isValidating,
      selectedFoodsEmpty: !isLoading && !data?.length,
      selectedFoodsCount: data?.length || 0,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetRestaurantSelectedFoods(restaurant_id?: string, search?: string, date_gte?: string, date_lte?: string) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/selectedfoods-foods/',
    shouldFetch(
      {
        params: {
          query: {
            restaurant: restaurant_id ? Number(restaurant_id) : undefined,
            search: search && search.length > 0 ? search : undefined,
            date_gte: date_gte && date_gte.length > 0 ? date_gte : undefined,
            date_lte: date_lte && date_lte.length > 0 ? date_lte : undefined,
          },
        },
      },
      restaurant_id
    )
  );

  const memoizedValue = useMemo(
    () => ({
      selectedFoods: data,
      selectedFoodsLoading: isLoading,
      selectedFoodsError: error,
      selectedFoodsValidating: isValidating,
      selectedFoodsEmpty: !isLoading && !data?.length,
      selectedFoodsCount: data?.length || 0,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetSelectedFoodsIngredients(
  search?: string,
  date_gte?: string,
  date_lte?: string
) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/selectedfoods-ingredients/',
    {
      params: {
        query: {
          search: search && search.length > 0 ? search : undefined,
          date_gte: date_gte && date_gte.length > 0 ? date_gte : undefined,
          date_lte: date_lte && date_lte.length > 0 ? date_lte : undefined,
        },
      },
    }
  );

  const memoizedValue = useMemo(
    () => ({
      selectedIngredients: data,
      selectedIngredientsLoading: isLoading,
      selectedIngredientsError: error,
      selectedIngredientsValidating: isValidating,
      selectedIngredientsEmpty: !isLoading && !data?.length,
      selectedIngredientsCount: data?.length || 0,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetRestaurantSelectedFoodsIngredients(
  restaurant_id?: string,
  search?: string,
  date_gte?: string,
  date_lte?: string
) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/selectedfoods-ingredients/',
    shouldFetch(
      {
        params: {
          query: {
            restaurant: restaurant_id ? Number(restaurant_id) : undefined,
            search: search && search.length > 0 ? search : undefined,
            date_gte: date_gte && date_gte.length > 0 ? date_gte : undefined,
            date_lte: date_lte && date_lte.length > 0 ? date_lte : undefined,
          },
        },
      },
      restaurant_id
    )
  );

  const memoizedValue = useMemo(
    () => ({
      selectedIngredients: data,
      selectedIngredientsLoading: isLoading,
      selectedIngredientsError: error,
      selectedIngredientsValidating: isValidating,
      selectedIngredientsEmpty: !isLoading && !data?.length,
      selectedIngredientsCount: data?.length || 0,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetSelectedFoodsCustomers(
  search?: string,
  date_gte?: string,
  date_lte?: string
) {
  const { data, isLoading, error, isValidating } = useQuery('/partners/selectedfoods-customers/', {
    params: {
      query: {
        search: search && search.length > 0 ? search : undefined,
        date_gte: date_gte && date_gte.length > 0 ? date_gte : undefined,
        date_lte: date_lte && date_lte.length > 0 ? date_lte : undefined,
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      selectedCustomers: data,
      selectedCustomersLoading: isLoading,
      selectedCustomersError: error,
      selectedCustomersValidating: isValidating,
      selectedCustomersEmpty: !isLoading && !data?.length,
      selectedCustomersCount: data?.length || 0,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetRestaurantSelectedFoodsCustomers(
  restaurant_id?: string,
  search?: string,
  date_gte?: string,
  date_lte?: string
) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/selectedfoods-customers/',
    shouldFetch(
      {
        params: {
          query: {
            restaurant: restaurant_id ? parseInt(restaurant_id, 10) : undefined,
            search: search && search.length > 0 ? search : undefined,
            date_gte: date_gte && date_gte.length > 0 ? date_gte : undefined,
            date_lte: date_lte && date_lte.length > 0 ? date_lte : undefined,
          },
        },
      },
      restaurant_id ? parseInt(restaurant_id, 10) : 0
    )
  );

  const memoizedValue = useMemo(
    () => ({
      selectedCustomers: data,
      selectedCustomersLoading: isLoading,
      selectedCustomersError: error,
      selectedCustomersValidating: isValidating,
      selectedCustomersEmpty: !isLoading && !data?.length,
      selectedCustomersCount: data?.length || 0,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Date format: YYYY-MM-DD
export function useGetSelectedFoodDetails(id: number, date: string) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/selectedfoods-foods/{id}/',
    shouldFetch(
      {
        params: {
          path: {
            id,
          },
          query: {
            date,
          },
        },
      },
      id
    )
  );

  const memoizedValue = useMemo(
    () => ({
      selectedFood: data,
      selectedFoodLoading: isLoading,
      selectedFoodError: error,
      selectedFoodValidating: isValidating,
      selectedFoodEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetSelectedFoodCustomerDetails(id: number, date: string) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/selectedfoods-customers/{id}/',
    shouldFetch(
      {
        params: {
          path: {
            id,
            pk: id,
          },
          query: {
            date,
          },
        },
      },
      Number(id)
    )
  );

  const memoizedValue = useMemo(
    () => ({
      selectedFoodCustomers: data,
      selectedFoodCustomersLoading: isLoading,
      selectedFoodCustomersError: error,
      selectedFoodCustomersValidating: isValidating,
      selectedFoodCustomersEmpty: !isLoading && !data,
      selectedFoodCustomersCount: data?.selected_foods.length || 0,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetDeliveries(
  search: string,
  date_gte: string | undefined,
  date_lte: string | undefined,
  page_size: number,
  page: number
) {
  const { data, isLoading, error, isValidating } = useQuery('/partners/deliveries/', {
    params: {
      query: {
        search: search && search.length > 0 ? search : undefined,
        date_gte: date_gte && date_gte.length > 0 ? date_gte : undefined,
        date_lte: date_lte && date_lte.length > 0 ? date_lte : undefined,
        page: page ? Number(page) : undefined,
        page_size: page_size ? Number(page_size) : undefined,
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      deliveries: data,
      deliveriesLoading: isLoading,
      deliveriesError: error,
      deliveriesValidating: isValidating,
      deliveriesEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetDeliveriesSummary() {
  const { data, isLoading, error, isValidating } = useQuery('/partners/deliveries/summary/');

  const memoizedValue = useMemo(
    () => ({
      deliveriesSummary: data,
      deliveriesSummaryLoading: isLoading,
      deliveriesSummaryError: error,
      deliveriesSummaryValidating: isValidating,
      deliveriesSummaryEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetDiscounts(
  search: string,
  subscription_created_at__date_gte: string,
  subscription_created_at__date_lte: string,
  page: number,
  page_size: number
) {
  const { data, isLoading, error, isValidating, mutate } = useQuery('/partners/discounts/', {
    params: {
      query: {
        search: search && search.length > 0 ? search : undefined,
        subscription_created_at__date_gte: subscription_created_at__date_gte && subscription_created_at__date_gte.length > 0 ? subscription_created_at__date_gte : undefined,
        subscription_created_at__date_lte: subscription_created_at__date_lte && subscription_created_at__date_lte.length > 0 ? subscription_created_at__date_lte : undefined,
        page: page ? Number(page) : undefined,
        page_size: page_size ? Number(page_size) : undefined,
      },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      discounts: data,
      discountsLoading: isLoading,
      discountsError: error,
      discountsValidating: isValidating,
      discountsEmpty: !isLoading && !data,
      refetchDiscounts: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetDiscount(id: number) {
  const { data, isLoading, error, isValidating } = useQuery('/partners/discounts/{id}/', {
    params: {
      path: { id },
    },
  });

  const memoizedValue = useMemo(
    () => ({
      discount: data,
      discountLoading: isLoading,
      discountError: error,
      discountValidating: isValidating,
      discountEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function createDiscount(data: any) {
  const { data: response, error } = await client.POST('/partners/discounts/', {
    body: data,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error creating discount');
  }

  return response;
}

export async function updateDiscount(id: number, data: any) {
  const { data: response, error } = await client.PATCH('/partners/discounts/{id}/', {
    params: {
      path: { id },
    },
    body: data,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error updating discount');
  }

  return response;
}

export async function updateSubscription(id: string | number | undefined, data: any) {
  if (!id) return { subscription: null };

  const { data: response, error } = await client.PATCH('/partners/subscriptions/{id}/', {
    params: {
      path: { id: Number(id) },
    },
    body: data,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error updating subscription');
  }

  return response;
}

export async function createSubscription(data: components["schemas"]["PartnersSubscriptionCreateRequest"]) {
  const { data: response, error } = await client.POST('/partners/subscriptions/', {
    body: data,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error creating subscription');
  }

  return response;
}

export async function getSubscriptionById(id: string | number) {
  if (!id) return null;

  const { data: response, error } = await client.GET('/partners/subscriptions/{id}/', {
    params: {
      path: { id: Number(id) },
    },
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error fetching subscription');
  }

  return response;
}

export async function pauseSubscription(id: string | number, data: components["schemas"]["PartnersSubscriptionPauseRequest"]) {
  if (!id) return null;

  const { data: response, error } = await client.POST('/partners/subscriptions/{id}/pause/', {
    params: {
      path: { id: Number(id) },
    },
    body: data,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error pausing subscription');
  }

  return response;
}

export async function unpauseSubscription(id: string | number, data: components["schemas"]["PartnersSubscriptionUnpauseRequest"]) {
  if (!id) return null;

  const { data: response, error } = await client.POST('/partners/subscriptions/{id}/unpause/', {
    params: {
      path: { id: Number(id) },
    },
    body: data,
  });

  if (error) {
    throw new Error(joinErrors(error) || 'Error unpausing subscription');
  }

  return response;
}

export function useGetRestaurantMealTagPlanTagPairs(restaurant_id: number) {
  const { data, isLoading, error, isValidating } = useQuery(
    '/partners/meals/{id}/',
    shouldFetch(
      {
        params: {
          path: {
            id: restaurant_id,
          },
        },
      },
      restaurant_id
    )
  );

  const memoizedValue = useMemo(
    () => ({
      mealPlanPairs: data?.pairs || [],
      mealPlanPairsLoading: isLoading,
      mealPlanPairsError: error,
      mealPlanPairsValidating: isValidating,
      mealPlanPairsEmpty: !isLoading && !data?.pairs.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetStartDate() {
  const { data, isLoading, error, isValidating } = useQuery('/start-date'  );

  const memoizedValue = useMemo(
    () => ({
      startDate: data?.start_date,
      startDateLoading: isLoading,
      startDateError: error,
      startDateValidating: isValidating,
      startDateEmpty: !isLoading && (!data || !data.start_date),
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}


